import moment from 'moment'
import { helpers, email as emailValidator, not } from 'vuelidate/lib/validators'
import emailSpellChecker from '@zootools/email-spell-checker'

export const moreThanYearsAgo = (years, format = 'YYYY-MM-DD') => helpers.withParams(
  { value: years },
  (value) => {
    const date = !isNaN(value) ? moment(value) : moment(value, format)
    const yearsAgo = moment().diff(date, 'years')
    return yearsAgo >= years
  }
)

export const isArrayOfEmails = (emails) => {
  if (!emails) return true
  let areAllEmails = true
  if (!Array.isArray(emails)) {
    emails = emails.split(';')
  }
  for (const email of emails) {
    if (!emailValidator(email)) {
      areAllEmails = false
    }
  }
  return areAllEmails
}

export const isPhone = (phone) => {
  if (!phone) return true
  if (typeof phone !== 'string') return false;
  if (!phone.includes('+')) return false

  // 1) Quick trim
  const raw = phone.trim()

  // 2) Must start with "+32" (case sensitive for '+', digits required)
  if (!raw.startsWith('+32')) return true

  // 3) Remove all common separators (space, dot, dash, parentheses)
  const normalized = raw.replace(/[\s.\-\(\)]/g, '');

  // 4) After normalization, ensure it's only + and digits
  if (!/^\+\d+$/.test(normalized)) return false

  // 5) Extract the part after +32
  const afterCC = normalized.slice(3); // remove "+32"

  // 6) The number MUST NOT start with '0' after the country code
  if (afterCC.startsWith('0')) return false

  // 7) Belgian national significant numbers are variable-length:
  //    accept 8 or 9 digits after +32 (covers landlines and mobiles).
  if (!/^\d{8,9}$/.test(afterCC)) return false;

  return true
}

export const isBirthdate = (value) => {
  if (!value) return true
  let format = 'DD/MM/YYYY'
  if (typeof value === 'object') {
    format = value.format
    value = value.value
  }
  const date = moment(value, format)
  if (!date.isValid()) return false
  if (date.isBefore(moment().subtract(100, 'years'))) return false
  if (date.isAfter(moment().subtract(1, 'days'))) return false
  return true
}

export const isNationalInsuranceNumber = (nationalInsuranceNumber, birthdate) => helpers.withParams({ birthdate }, () => {
  if (!nationalInsuranceNumber) return false

  const trimmedNationalInsuranceNumber = nationalInsuranceNumber.split(' ').join('').split('.').join('').split('-').join('').trim()
  if (trimmedNationalInsuranceNumber.length !== 11 || isNaN(trimmedNationalInsuranceNumber)) return false

  return true
})

export const isNationalInsuranceNumberValidWithBirthdate = (nationalInsuranceNumber, birthdate) => helpers.withParams({ birthdate }, () => {
  if (!nationalInsuranceNumber) return false

  const trimmedNationalInsuranceNumber = nationalInsuranceNumber.split(' ').join('').split('.').join('').split('-').join('').trim()

  const ninBirthdateDigits = trimmedNationalInsuranceNumber.slice(0, 6)
  let birthdateDigits = false

  if (isNaN(birthdate)) {
    birthdateDigits = moment(birthdate, 'DD/MM/YYYY').format('YYMMDD')
  } else {
    birthdateDigits = moment.unix(birthdate).format('YYMMDD')
  }

  return ninBirthdateDigits === birthdateDigits
})

export const isValidName = (name) => {
  if (String(name) < 1) return true // Avoid checking optional fields
  if (String(name).length < 2) return false // More characters than 1

  const pattern = /^[\p{L}\s'-]+$/u
  return pattern.test(name) !== false;
}

export const isValidNationalInsuranceNumber = (nationalInsuranceNumber, birthdate, allowEmpty = false, checkBirthdate = true, checkControlNumber = true) => {
  if (allowEmpty && !nationalInsuranceNumber) return true
  if (!nationalInsuranceNumber) return false
  const trimmedNationalInsuranceNumber = nationalInsuranceNumber.split(' ').join('').split('.').join('').split('-').join('').trim()
  if (!nationalInsuranceNumber || trimmedNationalInsuranceNumber.length !== 11) return false

  const ninBirthdateDigits = trimmedNationalInsuranceNumber.slice(0, 6)
  let birthdateDigits = ''
  if (isNaN(birthdate)) {
    birthdateDigits = moment(birthdate, 'DD/MM/YYYY').format('YYMMDD')
  } else {
    birthdateDigits = moment.unix(birthdate).format('YYMMDD')
  }

  if (checkBirthdate && ninBirthdateDigits !== birthdateDigits) return false

  let birthdateYear = ''
  if (isNaN(birthdate)) {
    birthdateYear = moment(birthdate, 'DD/MM/YYYY').format('YYYY')
  } else {
    birthdateYear = moment.unix(birthdate).format('YYYY')
  }
  const sexNums = trimmedNationalInsuranceNumber.slice(6, 9);
  const controlNumber = trimmedNationalInsuranceNumber.slice(9, 11);
  const birthdateAndSexNums = ((birthdateYear >= 2000 ? '2' : '') + birthdateDigits + sexNums);
  const calculatedControlNumber = 97 - (birthdateAndSexNums % 97);

  if (checkControlNumber && (parseInt(calculatedControlNumber) !== parseInt(controlNumber))) return false

  return true
}
